/**
 * ChatStore — owns chat engines (LLMClient) keyed by chatId, decoupled from the
 * React component tree and from "which chat is currently displayed".
 *
 * Why this exists (root cause it fixes):
 * Production serves SSE via a sync generator through a2wsgi, which gives each
 * gunicorn worker only ~10 WSGI threads; every SSE stream pins one for its whole
 * life and isn't reaped on disconnect. The UI-coupled connection lifecycle
 * (navigate-away → clear() → streamId=null → useSSEListener closes → navigate-back
 * → reconnect) spawns multiple generators per turn and exhausts that pool, which
 * CloudFront surfaces as a 503. The cure (per Tim): DON'T close the old stream on
 * navigation. The connection's lifecycle belongs to the TURN, not the view.
 *
 * The store holds engines so a live connection survives navigation: switching
 * chats is `setActive(chatId)` — a pure selection that never closes/clears/cancels
 * a live engine. The UI still renders a single "active" chat; the store provides it.
 *
 * Engine ownership of the SSE (so the connection truly survives) is wired by the
 * provider; this module is the registry + lifecycle policy and carries no React.
 */
import { makeObservable, observable, computed, action } from "mobx";

// Placeholder key for a brand-new chat that has no server chatId yet. The
// engine streams under this key; useConversationPersistence calls store.rekey(NEW_CHAT_ID,
// mintedId) once the backend mints the real conversation id.
export const NEW_CHAT_ID = "__new__";

export class ChatStore {
  /**
   * @param {(chatId: string) => object} engineFactory builds an engine for a chat.
   * @param {{maxIdle?: number}} [opts] cap on retained IDLE (non-active, non-live) engines.
   */
  constructor(engineFactory, opts = {}) {
    if (typeof engineFactory !== "function") {
      throw new Error("ChatStore requires an engineFactory(chatId)");
    }
    this._factory = engineFactory;
    this._engines = new Map(); // chatId -> engine (insertion-ordered, NOT observable)
    this._activeChatId = null;
    this._liveChatId = null;   // chat whose turn is streaming (drives the SSE listener)
    this._maxIdle = typeof opts.maxIdle === "number" ? opts.maxIdle : 3;
    // Only the active-chat pointer is observable: a React observer re-renders to
    // the new active engine on setActive/rekey. The engine Map is plain — the
    // engines are themselves MobX-observable, so their turns/streamId stay
    // reactive when rendered.
    makeObservable(this, {
      _activeChatId: observable,
      _liveChatId: observable,
      active: computed,
      activeChatId: computed,
      liveEngine: computed,
      setActive: action,
      setLive: action,
      setLiveEngine: action,
      rekey: action,
      release: action,
      disposeAll: action,
    });
  }

  /**
   * Mark which chat's turn is streaming. The provider runs a single SSE
   * listener bound to `liveEngine`, NOT to the displayed engine — so
   * navigating to view another chat never re-binds or closes the live stream.
   * Set when a turn starts; the listener auto-closes once the engine's streamId
   * clears (see `liveEngine`).
   */
  setLive(chatId) {
    this._liveChatId = chatId || null;
    if (chatId) this.getEngine(chatId);
  }

  /**
   * Mark live by ENGINE reference (robust to new-chat/rekey timing — no chatId
   * needed). Looks up the engine's current key. Used by useConversationPersistence when an
   * engine's streamId goes truthy.
   */
  setLiveEngine(engine) {
    if (!engine) { this._liveChatId = null; return; }
    for (const [id, e] of this._engines) {
      if (e === engine) { this._liveChatId = id; return; }
    }
  }

  /** The engine whose turn is currently streaming, or null. */
  get liveEngine() {
    if (!this._liveChatId) return null;
    const e = this._engines.get(this._liveChatId);
    return e && (e.streamId || e.active) ? e : null;
  }

  /** Return the engine for a chat, creating it lazily. null for a falsy id. */
  getEngine(chatId) {
    if (!chatId) return null;
    let engine = this._engines.get(chatId);
    if (!engine) {
      engine = this._factory(chatId);
      this._engines.set(chatId, engine);
    }
    return engine;
  }

  /**
   * Select which chat the UI renders. Pure selection — it does NOT close, clear,
   * or cancel the previously-active engine. A live stream on the old chat keeps
   * running in the store. Idle engines beyond the cap are evicted.
   */
  setActive(chatId) {
    this._activeChatId = chatId || null;
    if (chatId) this.getEngine(chatId);
    this._evictIdle();
    return this.active;
  }

  get activeChatId() {
    return this._activeChatId;
  }

  get active() {
    return this._activeChatId ? this._engines.get(this._activeChatId) || null : null;
  }

  has(chatId) {
    return this._engines.has(chatId);
  }

  /** True if the chat has an in-flight turn (open connection): streamId or active. */
  isLive(chatId) {
    const e = this._engines.get(chatId);
    return !!(e && (e.streamId || e.active));
  }

  liveChatIds() {
    const ids = [];
    for (const [id, e] of this._engines) {
      if (e.streamId || e.active) ids.push(id);
    }
    return ids;
  }

  /**
   * Evict an engine ONLY if it's idle (no live turn) and not the active one.
   * Live or active engines are never disposed here — that's the guarantee that
   * navigation can't kill a running stream. Returns true if evicted.
   */
  release(chatId) {
    const e = this._engines.get(chatId);
    if (!e) return false;
    if (chatId === this._activeChatId) return false;
    if (e.streamId || e.active) return false;
    try { e.dispose?.(); } catch (_) { /* never throw on teardown */ }
    this._engines.delete(chatId);
    return true;
  }

  /**
   * New-chat flow: an engine started under a placeholder id (no chatId yet) is
   * re-keyed once the backend mints the real conversation id. The active
   * selection follows.
   */
  rekey(fromId, toId) {
    if (!fromId || !toId || fromId === toId) return;
    const e = this._engines.get(fromId);
    if (!e) return;
    this._engines.delete(fromId);
    this._engines.set(toId, e);
    if (this._activeChatId === fromId) this._activeChatId = toId;
    if (this._liveChatId === fromId) this._liveChatId = toId;
  }

  disposeAll() {
    for (const [, e] of this._engines) {
      try { e.dispose?.(); } catch (_) { /* ignore */ }
    }
    this._engines.clear();
    this._activeChatId = null;
    this._liveChatId = null;
  }

  // Drop oldest IDLE (non-active, non-live) engines beyond the cap. Live and
  // active engines are always retained.
  _evictIdle() {
    const idle = [];
    for (const id of this._engines.keys()) {
      if (id !== this._activeChatId && !this.isLive(id)) idle.push(id);
    }
    while (idle.length > this._maxIdle) {
      this.release(idle.shift());
    }
  }
}

export default ChatStore;
